/**
 * @file User Group Request Controller component
 * Manages join/leave group logic and provides extensible UI controls
 * @version 1.0.0
 * @since 2025-09-05
 */

import { useUser } from '../composables/user.js';
import { useNotifications, useErrorHandler } from '../../core/errors.js';

export default {
  name: 'UserGroupRequestController',

  props: {
    groupId: {
      type: String,
      required: true,
    },
  },

  template: `
    <component
      :is="controlComponent"
      v-bind="controlProps"
      @join="joinGroupAction"
      @leave="leaveGroupAction" 
      @cancel="cancelRequestAction"
    />
  `,

  setup(props) {
    const user = useUser();
    const { info, warning } = useNotifications();
    const { handleError } = useErrorHandler();

    // Get group information
    const group = Vue.computed(() =>
      user.availableGroups.find(g => g.id === props.groupId)
    );

    // Determine the state of the group for the current user
    const groupState = Vue.computed(() => {
      // Check if request is pending
      if (
        user.pendingGroupRequests.some(
          req => req.requestedGroupName === props.groupId
        )
      ) {
        return 'pending';
      }

      // Check if user is a member
      if (user.userGroups.includes(props.groupId)) {
        return 'member';
      }

      return 'not_member';
    });

    // Get pending request for this group
    const pendingRequest = Vue.computed(() =>
      user.pendingGroupRequests.find(
        req => req.requestedGroupName === props.groupId
      )
    );

    const joinGroupAction = async metadata => {
      try {
        if (!user.loggedIn) {
          warning('Cannot join groups when not logged in');
          return;
        }

        const result = await user.joinGroup(props.groupId, metadata);

        // Handle different response statuses
        switch (result.status) {
          case 'granted':
            info(
              result.message || `Joined ${group.value?.name || props.groupId}`
            );
            break;
          case 'pending':
            info(
              result.message ||
                `Request submitted for ${group.value?.name || props.groupId}`
            );
            break;
          case 'denied':
            warning(
              result.message ||
                `Access denied to ${group.value?.name || props.groupId}`
            );
            break;
          case 'cancelled':
            // User cancelled the request (e.g., justification modal)
            break;
        }
      } catch (err) {
        handleError(err, { operation: 'joinGroup', groupId: props.groupId });
      }
    };

    const leaveGroupAction = async metadata => {
      try {
        if (!user.loggedIn) {
          warning('Cannot leave groups when not logged in');
          return;
        }

        const result = await user.leaveGroup(props.groupId, metadata);

        switch (result.status) {
          case 'granted':
            info(
              result.message || `Left ${group.value?.name || props.groupId}`
            );
            break;
          case 'pending':
            info(
              result.message ||
                `Leave request submitted for ${group.value?.name || props.groupId}`
            );
            break;
          case 'denied':
            warning(
              result.message ||
                `Cannot leave ${group.value?.name || props.groupId}`
            );
            break;
          case 'cancelled':
            // User cancelled the request
            break;
        }
      } catch (err) {
        handleError(err, { operation: 'leaveGroup', groupId: props.groupId });
      }
    };

    const cancelRequestAction = async () => {
      const request = pendingRequest.value;
      if (!request) return;

      // Try to cancel with handler if available
      if (user.cancelGroupRequest) {
        const success = await user.cancelGroupRequest(request.id);
        if (success) {
          info(`Cancelled request for ${group.value?.name || props.groupId}`);
        } else {
          warning(
            `Could not cancel request for ${group.value?.name || props.groupId}`
          );
        }
      }
    };

    // Default controls component
    const DefaultControls = {
      props: {
        groupState: String,
        group: Object,
        user: Object,
        pendingRequest: Object,
      },
      template: `
        <div class="text-end">
          <!-- Current Member -->
          <div v-if="groupState === 'member'">
            <span class="badge bg-success mb-2">
              <i class="bi bi-check-circle"></i> Member
            </span>
            <div>
              <button
                class="btn btn-sm btn-outline-danger"
                @click="$emit('leave')"
                :disabled="!user.loggedIn"
              >
                <i class="bi bi-box-arrow-right"></i> Leave
              </button>
            </div>
          </div>
          
          <!-- Pending Request -->
          <div v-else-if="groupState === 'pending'">
            <span class="badge bg-warning mb-2">
              <i class="bi bi-clock"></i> Pending
            </span>
            <div>
              <button
                class="btn btn-sm btn-outline-secondary"
                @click="$emit('cancel')"
                :disabled="user.isLoading"
              >
                <i class="bi bi-x"></i> Cancel
              </button>
            </div>
          </div>
          
          <!-- Not Member -->
          <div v-else>
            <button
              class="btn btn-sm btn-outline-primary"
              @click="$emit('join')"
              :disabled="!user.loggedIn"
            >
              <i class="bi bi-plus-circle"></i> Join Group
            </button>
          </div>
        </div>
      `,
      emits: ['join', 'leave', 'cancel'],
    };

    // Get custom component from user's group request handler
    const customComponent = Vue.computed(
      () => user.groupRequestHandler?.customUserGroupRequestController || null
    );

    // Determine which component to use
    const controlComponent = Vue.computed(
      () => customComponent.value || DefaultControls
    );

    // Props to pass to the control component
    const controlProps = Vue.computed(() => ({
      groupState: groupState.value,
      group: group.value,
      user: user,
      pendingRequest: pendingRequest.value,
      groupId: props.groupId,
    }));

    return {
      controlComponent,
      controlProps,
      joinGroupAction,
      leaveGroupAction,
      cancelRequestAction,
    };
  },
};
